/**
 * PoundFun - lightweight "Select Options" popover for product cards.
 *
 * Multi-variant product cards render a button[data-pf-options] plus a sibling
 * <script class="pf-options-data"> containing {"names": [...], "variants": [...]}.
 * Clicking the button opens a small modal with just the variant selectors and an
 * add-to-cart button (no full product-page injection). Adding posts to
 * /cart/add.js and refreshes/opens the cart drawer.
 *
 * Document-level delegation so it works for cards inside sliders and after AJAX
 * re-renders. The button keeps the .quick-add__submit class for styling; it has
 * no data-vid, so dynamic-add-to-cart.js's guard ignores it.
 */
(function () {
  'use strict';

  var overlay = null;

  function money(pence) {
    return '£' + (Number(pence) / 100).toFixed(2);
  }

  function buildOverlay() {
    overlay = document.createElement('div');
    overlay.className = 'pf-options-overlay';
    overlay.innerHTML =
      '<div class="pf-options-dialog" role="dialog" aria-modal="true" aria-label="Choose options">' +
      '<button type="button" class="pf-options-close" aria-label="Close">&#10005;</button>' +
      '<div class="pf-options-body"></div>' +
      '</div>';
    document.body.appendChild(overlay);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    overlay.querySelector('.pf-options-close').addEventListener('click', close);
  }

  function close() {
    if (overlay) {
      overlay.classList.remove('active');
      document.body.classList.remove('pf-options-open');
    }
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function open(btn) {
    var dataEl = btn.parentElement.querySelector('.pf-options-data');
    if (!dataEl) {
      // No data for some reason - fall back to the product page.
      window.location.href = btn.getAttribute('data-pf-url');
      return;
    }
    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      window.location.href = btn.getAttribute('data-pf-url');
      return;
    }
    if (!overlay) buildOverlay();
    renderBody(data, {
      title: btn.getAttribute('data-pf-title') || '',
      image: btn.getAttribute('data-pf-image') || '',
      url: btn.getAttribute('data-pf-url') || ''
    });
    overlay.classList.add('active');
    document.body.classList.add('pf-options-open');
  }

  function renderBody(data, meta) {
    var body = overlay.querySelector('.pf-options-body');
    var names = data.names || [];
    var variants = data.variants || [];

    var html = '<div class="pf-options-head">';
    if (meta.image) {
      html += '<img class="pf-options-img" src="' + escapeHtml(meta.image) + '" alt="">';
    }
    html += '<div class="pf-options-head-text">';
    html += '<a class="pf-options-title" href="' + escapeHtml(meta.url) + '">' + escapeHtml(meta.title) + '</a>';
    html += '<div class="pf-options-price"></div>';
    html += '</div></div>';

    html += '<div class="pf-options-selectors">';
    names.forEach(function (name, i) {
      var values = [];
      variants.forEach(function (v) {
        var val = v.options[i];
        if (val != null && values.indexOf(val) === -1) values.push(val);
      });
      html += '<label class="pf-options-field"><span class="pf-options-field-label">' + escapeHtml(name) + '</span>';
      html += '<select class="pf-options-select" data-opt-index="' + i + '">';
      values.forEach(function (val) {
        html += '<option value="' + escapeHtml(val) + '">' + escapeHtml(val) + '</option>';
      });
      html += '</select></label>';
    });
    html += '</div>';

    html += '<button type="button" class="pf-options-add pf-button-primary">Add to cart</button>';
    html += '<a class="pf-options-viewlink" href="' + escapeHtml(meta.url) + '">View full details</a>';
    body.innerHTML = html;

    var selects = Array.prototype.slice.call(body.querySelectorAll('select[data-opt-index]'));
    var addBtn = body.querySelector('.pf-options-add');
    var priceEl = body.querySelector('.pf-options-price');

    function currentVariant() {
      var chosen = [];
      selects.forEach(function (s) {
        chosen[parseInt(s.getAttribute('data-opt-index'), 10)] = s.value;
      });
      return variants.find(function (v) {
        return v.options.every(function (val, i) {
          return val === chosen[i];
        });
      });
    }

    function update() {
      var v = currentVariant();
      if (!v) {
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
        priceEl.textContent = '';
        return;
      }
      priceEl.textContent = money(v.price);
      if (v.available) {
        addBtn.disabled = false;
        addBtn.textContent = 'Add to cart';
      } else {
        addBtn.disabled = true;
        addBtn.textContent = 'Sold out';
      }
    }

    selects.forEach(function (s) {
      s.addEventListener('change', update);
    });
    update();

    addBtn.addEventListener('click', function () {
      var v = currentVariant();
      if (!v || !v.available) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: v.id, quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) return Promise.reject();
          return r.json();
        })
        .then(function () {
          close();
          refreshCart();
        })
        .catch(function () {
          addBtn.disabled = false;
          addBtn.textContent = 'Try again';
        });
    });
  }

  function refreshCart() {
    var cd = document.querySelector('cart-drawer');
    if (cd) {
      fetch('/cart?section_id=cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (htmlText) {
          var doc = new DOMParser().parseFromString(htmlText, 'text/html');
          var fresh = doc.querySelector('#CartDrawer');
          var cur = document.querySelector('#CartDrawer');
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
          cd.classList.remove('is-empty');
          document.dispatchEvent(new Event('updateShippingBar'));
          document.dispatchEvent(new Event('updatePromoBar'));
          if (typeof cd.open === 'function') cd.open();
        })
        .catch(function () {});
    } else {
      window.location.href = '/cart';
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-pf-options]');
    if (!btn) return;
    e.preventDefault();
    open(btn);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });
})();
